import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import { GroupViewSwitch } from '../molecules/GroupViewSwitch'
import React, { FC, useMemo } from 'react'
import { GroupViewType } from '../../lib/entities'
import { RPUser, colors } from '@hazadapt-git/public-core-base'
import { TeamMemberView } from '../molecules/TeamMemberView'
import { makeStyles } from 'tss-react/mui'
import {
    canUserAddMembers,
    isUserAdmin,
    searchTeam,
    sortOrgMembers,
    useWindowSizeUp,
} from '../../lib/utils'
import { IoAdd, IoSearch } from 'react-icons/io5'
import { AddSeatsTriggerAndDrawer } from './AddSeatsTriggerAndDrawer'
import { useOrganization } from '../../lib/hooks/useOrganization'
import pluralize from 'pluralize'
import { TextField } from '../atoms'
import { Box } from '@mui/system'
import { primaryIconSize } from '../../lib/styles/universal'
import { AdminIcon, OwnerIcon } from '../molecules'
import Stack from '@mui/material/Stack'

export interface TeamMemberListProps {
    members: RPUser[]
    seats: number
    onAddUserClick(): void
    mode?: 'setup' | 'manage'
    activeUser: RPUser
}

export const TeamMemberList: FC<TeamMemberListProps> = (
    props: TeamMemberListProps
) => {
    const [groupView, setGroupView] = React.useState<GroupViewType>('card')
    const [searchQuery, setSearchQuery] = React.useState<string>('')
    const seatsRemaining = Math.max(props.seats - props.members.length, 0)
    const { classes: localClasses } = useLocalStyles()
    const mdScreens = useWindowSizeUp('md')
    const { unresolvedSeatCount } = useOrganization()

    const adminUser = useMemo(
        () => isUserAdmin(props.activeUser),
        [props.activeUser]
    )

    const sortedMembers = useMemo(() => {
        const members = [...props.members]
        return members.sort(sortOrgMembers)
    }, [props.members])

    const [filteredTeam, setFilteredTeam] =
        React.useState<RPUser[]>(sortedMembers)

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            if (searchQuery.trim() === '') {
                setFilteredTeam(sortedMembers)
            } else {
                const { team: filteredTeam } = searchTeam(
                    sortedMembers,
                    searchQuery
                )
                setFilteredTeam(filteredTeam)
            }
        }, 300)

        return () => clearTimeout(timeout)
    }, [searchQuery, sortedMembers])

    const memberList = React.useCallback(() => {
        return groupView === 'card' || !mdScreens ? (
            <Grid container spacing={2}>
                {filteredTeam.length > 0 ? (
                    filteredTeam.map((user) => (
                        <Grid
                            item
                            xs={12}
                            sm={4}
                            lg={3}
                            xl={2}
                            key={user.rp_user_id}
                        >
                            <TeamMemberView
                                targetUser={user}
                                groupView="card"
                                activeUser={props.activeUser}
                            />
                        </Grid>
                    ))
                ) : (
                    <Grid item xs={12} my="2rem">
                        <Typography fontWeight={500}>
                            No team members found.
                        </Typography>
                    </Grid>
                )}
            </Grid>
        ) : (
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Typography fontWeight={500} textAlign="left">
                                Name
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography fontWeight={500}>Title</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography fontWeight={500}>
                                Email Address
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography fontWeight={500}>Date Added</Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredTeam.length > 0 ? (
                        filteredTeam.map((user) => (
                            <TeamMemberView
                                key={user.rp_user_id}
                                targetUser={user}
                                groupView={groupView}
                                activeUser={props.activeUser}
                            />
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={4} sx={{ borderBottom: 0 }}>
                                <Typography
                                    fontWeight={500}
                                    mt="1rem"
                                    mb="2rem"
                                >
                                    No team members found.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        )
    }, [props.activeUser, groupView, mdScreens, filteredTeam])

    return (
        <Grid
            container
            columnSpacing={2}
            rowSpacing={1}
            ml="-0.5rem"
            className={localClasses.container}
        >
            <Grid item xs={12} md={3}>
                <Typography
                    variant="h3"
                    component="h2"
                    color={colors.grays.NOIR}
                    fontWeight={500}
                    mr={{ md: '1rem' }}
                >
                    {props.mode === 'setup' ? 'Add ' : null}
                    Team Members
                </Typography>
            </Grid>
            <Grid item xs={adminUser ? 7 : 12} md={adminUser ? 5 : 9}>
                <Typography color={colors.primary.CERULEAN} fontStyle="italic">
                    {seatsRemaining} of {props.seats} seats remaining
                </Typography>
            </Grid>
            {adminUser && (
                <>
                    <Grid
                        item
                        xs={5}
                        md={4}
                        display="flex"
                        justifyContent="flex-end"
                    >
                        <AddSeatsTriggerAndDrawer
                            variant={mdScreens ? 'outlined' : 'link'}
                            buttonText="Buy More Seats"
                        />
                    </Grid>
                    {unresolvedSeatCount > 0 && (
                        <Grid item xs={12}>
                            <Alert
                                severity="warning"
                                sx={{ width: 'fit-content' }}
                            >
                                <AlertTitle>Heads up!</AlertTitle>
                                <Typography fontWeight={500}>
                                    You have more team members than available
                                    seats.
                                </Typography>
                                <Typography variant="body2">
                                    Please remove {unresolvedSeatCount} more
                                    team{' '}
                                    {pluralize('member', unresolvedSeatCount)}{' '}
                                    from your organization or buy new seats for
                                    them.
                                </Typography>
                            </Alert>
                        </Grid>
                    )}
                </>
            )}
            <Grid item xs={12} md={6} className={localClasses.search}>
                <TextField
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: <IoSearch size={primaryIconSize} />,
                        placeholder: 'Search',
                    }}
                    sx={{ maxWidth: '100%', flexGrow: 1 }}
                />
            </Grid>
            {canUserAddMembers(props.activeUser) && (
                <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ display: 'flex', alignItems: 'flex-end' }}
                >
                    <Tooltip
                        title="Please purchase more seats to add another team member"
                        disableHoverListener={seatsRemaining > 0}
                        disableFocusListener={seatsRemaining > 0}
                    >
                        <div>
                            <Button
                                onClick={props.onAddUserClick}
                                variant="link"
                                startIcon={
                                    <Box
                                        sx={{
                                            border: '1px solid currentColor',
                                            borderRadius: '4px',
                                            width: '2rem',
                                            height: '2rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <IoAdd size="1.25rem" />
                                    </Box>
                                }
                                sx={{ textDecoration: 'none !important' }}
                                disabled={seatsRemaining <= 0}
                            >
                                Add Team Member
                            </Button>
                        </div>
                    </Tooltip>
                </Grid>
            )}
            <Grid item xs>
                <Stack gap="0.25rem" display="grid">
                    <OwnerIcon showType />
                    <AdminIcon showType />
                </Stack>
            </Grid>
            {mdScreens && (
                <Grid
                    item
                    xs="auto"
                    className={localClasses.seats}
                    sx={{ alignItems: mdScreens ? 'end' : 'start' }}
                >
                    <GroupViewSwitch
                        state={groupView}
                        onSwitch={setGroupView}
                    />
                </Grid>
            )}
            <Grid item xs={12} order={5}>
                {memberList()}
            </Grid>
        </Grid>
    )
}

const useLocalStyles = makeStyles()({
    container: {
        width: '100%',
        paddingTop: '1rem',
        alignItems: 'center',
    },
    search: {
        display: 'flex',
        flexDirection: 'column',
        gap: '.5rem',
    },
    team: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    sub: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyItems: 'center',
        gap: '1rem',
        justifyContent: 'space-between',
        paddingBottom: '1rem',
    },
    labels: {
        justifyItems: 'center',
        flexGrow: 1,
        padding: '1rem 2rem',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr',
    },
    switch: {
        paddingBottom: '1.5rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '2rem',
        width: '100%',
    },
    seats: {
        display: 'flex',
        flexDirection: 'column',
        gap: '.5rem',
    },
})
